import React from "react";
import { Page, Text, View, Document, Image, Link, StyleSheet } from "@react-pdf/renderer";

import { styles as base } from "./styles";
import { SectionSeparator } from "./components";
import { handleAvailability, constructObjective } from "../../helpers/candidate";
import { latestFirst, handleTimePeriod, readDob, isRealDate } from "../../helpers/date";
import { cleanProfileText } from "../../helpers/misc";

const colors = {
    midnight:  "#0B1729",
    ink:       "#1A1F2E",
    inkMuted:  "#6A7280",
    inkSubtle: "#8B92A0",
    rule:      "#D7DAE0",
    surface:   "#FFFFFF",
    coral:     "#E85533",
    link:      "#1F5C8C",
    railBg:    "#F7F8FA",
};

const styles = StyleSheet.create({
    page: {
        flexDirection: "row",
        backgroundColor: colors.surface,
        fontFamily: "SourceSansPro",
        color: colors.ink,
        paddingTop: 38,
        paddingBottom: 38,
    },
    railBackground: {
        position: "absolute",
        left: 0,
        top: -38,
        bottom: -38,
        width: 200,
        backgroundColor: colors.railBg,
    },
    rail: {
        width: 200,
        paddingHorizontal: 24,
    },
    body: {
        flex: 1,
        paddingHorizontal: 32,
    },

    railPhotoContainer: {
        alignItems: "center",
        marginBottom: 18,
    },
    railPhoto: {
        width: 152,
        height: 152,
        objectFit: "cover",
    },

    bodyName: {
        fontFamily: "SourceSansPro",
        fontWeight: "bold",
        fontSize: 26,
        letterSpacing: 0.5,
        color: colors.midnight,
        lineHeight: 1.1,
    },
    bodyRole: {
        fontFamily: "SourceSansPro",
        fontWeight: "bold",
        fontSize: 10,
        letterSpacing: 1.6,
        marginTop: 4,
        marginBottom: 6,
        color: colors.coral,
    },

    railSectionTitle: {
        fontFamily: "SourceSansPro",
        fontWeight: "bold",
        fontSize: 9,
        letterSpacing: 1.6,
        color: colors.midnight,
        marginTop: 18,
        marginBottom: 4,
    },
    railSectionRule: {
        height: 1.5,
        width: 18,
        backgroundColor: colors.coral,
        marginBottom: 8,
    },
    railLabel: {
        fontFamily: "SourceSansPro",
        fontSize: 8.5,
        letterSpacing: 0.4,
        color: colors.inkMuted,
        marginTop: 6,
    },
    railValue: {
        fontFamily: "SourceSansPro",
        fontSize: 10,
        color: colors.ink,
        marginTop: 1,
    },
    railValueLink: {
        fontFamily: "SourceSansPro",
        fontSize: 10,
        color: colors.link,
        marginTop: 1,
        textDecoration: "none",
    },

    railBullet: {
        flexDirection: "row",
        marginBottom: 4,
        alignItems: "flex-start",
    },
    railBulletDot: {
        fontFamily: "SourceSansPro",
        fontSize: 10,
        color: colors.coral,
        width: 8,
    },
    railBulletText: {
        flex: 1,
        fontFamily: "SourceSansPro",
        fontSize: 10,
        color: colors.ink,
        lineHeight: 1.4,
    },
    railBulletDate: {
        fontFamily: "SourceSansPro",
        fontSize: 7.5,
        color: colors.inkSubtle,
        marginTop: 2,
    },

    bodySectionWrap: {
        marginBottom: 4,
    },
    bodyTimelineEntry: {
        paddingBottom: 12,
    },
    bodyTimelineHeader: {
        flexDirection: "row",
        alignItems: "flex-start",
        paddingBottom: 3,
    },
    bodyTimelineHeaderMain: {
        flex: 1,
        paddingRight: 14,
    },
    bodyTimelineRole: {
        fontFamily: "SourceSansPro",
        fontWeight: "bold",
        fontSize: 10.5,
        color: colors.midnight,
        letterSpacing: 0.2,
    },
    bodyTimelineCompany: {
        fontFamily: "SourceSansPro",
        fontSize: 10.5,
        color: colors.inkMuted,
        marginTop: 1,
    },
    bodyTimelineDateBox: {
        width: 100,
        alignItems: "flex-end",
        paddingTop: 2,
    },
    bodyTimelineDate: {
        fontFamily: "SourceSansPro",
        fontSize: 9.5,
        color: colors.inkMuted,
        letterSpacing: 0.3,
    },
    bodyTimelineDescription: {
        fontFamily: "SourceSansPro",
        fontSize: 10.5,
        color: colors.ink,
        lineHeight: 1.45,
        marginTop: 3,
    },

    bodyReferenceItem: {
        paddingTop: 6,
        paddingBottom: 8,
        borderBottomWidth: 0.5,
        borderBottomColor: colors.rule,
    },
});

const RailSection = ({ title, accentColor, children }) => {
    const ruleStyle = accentColor ? [styles.railSectionRule, { backgroundColor: accentColor }] : styles.railSectionRule;
    return (
        <View wrap={false} minPresenceAhead={40}>
            <Text style={styles.railSectionTitle}>{(title || "").toUpperCase()}</Text>
            <View style={ruleStyle} />
            {children}
        </View>
    );
};

const RailKV = ({ label, value, href }) => {
    if (!value) return null;
    return (
        <View>
            <Text style={styles.railLabel}>{(label || "").toUpperCase()}</Text>
            {href ? (
                <Link style={styles.railValueLink} href={href}>{value}</Link>
            ) : (
                <Text style={styles.railValue}>{value}</Text>
            )}
        </View>
    );
};

const RailBullet = ({ text, date, accentColor }) => {
    const dotStyle = accentColor ? [styles.railBulletDot, { color: accentColor }] : styles.railBulletDot;
    return (
        <View style={styles.railBullet}>
            <Text style={dotStyle}>•</Text>
            <View style={{ flex: 1 }}>
                <Text style={styles.railBulletText}>{text}</Text>
                {isRealDate(date) ? <Text style={styles.railBulletDate}>exp. {date}</Text> : null}
            </View>
        </View>
    );
};

const handleVisaList = (profile) => {
    const out = [];
    if (profile?.b1b2?.status) out.push({ label: "B1/B2", date: profile.b1b2.expiryDate });
    if (profile?.c1d?.status) out.push({ label: "C1/D", date: profile.c1d.expiryDate });
    if (profile?.schengen?.status) out.push({ label: "Schengen", date: profile.schengen.expiryDate });
    if (profile?.greenCard?.status) out.push({ label: "Green Card", date: profile.greenCard.expiryDate });
    return out;
};

export const MyDocumentTwoColumn = ({ user, sections, accentColor, profileImageSrc }) => {

    const showSection = (key) => sections ? sections[key] !== false : true;
    const p = user?.profile || {};
    const visas = handleVisaList(p);
    const roleName = p.lookingFor?.desiredRoles?.[0]?.position?.[0]?.name;
    const dobValue = p.dob ? readDob(p.dob) : null;
    const roleStyle = accentColor ? [styles.bodyRole, { color: accentColor }] : styles.bodyRole;
    const photoSrc = profileImageSrc || p.profileImage;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* rail background — sticks to the page edges on every page */}
                <View fixed style={styles.railBackground} />

                {/* ============ LEFT RAIL ============ */}
                <View style={styles.rail}>
                    {photoSrc ? (
                        <View style={styles.railPhotoContainer}>
                            <Image style={styles.railPhoto} source={photoSrc} />
                        </View>
                    ) : null}

                    <RailSection accentColor={accentColor} title="Contact">
                        <RailKV label="Location" value={p.currentLocation?.name} />
                        <RailKV label="Email" value={p.email} href={p.email ? `mailto:${p.email}` : null} />
                        <RailKV label="Mobile" value={p.primaryPhone} />
                        <RailKV label="Other" value={p.secondaryPhone} />
                    </RailSection>

                    <RailSection accentColor={accentColor} title="Personal">
                        <RailKV label="Availability" value={handleAvailability(p)} />
                        {dobValue ? <RailKV label="Date of Birth" value={dobValue} /> : null}
                        {p.nationality?.length ? <RailKV label="Nationality" value={p.nationality.join(", ")} /> : null}
                        {p.gender ? <RailKV label="Gender" value={p.gender} /> : null}
                        {p.height ? <RailKV label="Height" value={`${p.height}cm`} /> : null}
                        {p.languages?.length ? <RailKV label="Languages" value={p.languages.join(", ")} /> : null}
                        {p.tattoo ? <RailKV label="Tattoos" value={p.tattoo} /> : null}
                        {typeof p.smoking === "boolean" ? <RailKV label="Smoking" value={p.smoking ? "Smoker" : "Non Smoker"} /> : null}
                        {p.relationshipStatus ? <RailKV label="Status" value={p.relationshipStatus} /> : null}
                        {p.country ? <RailKV label="Residence" value={p.country} /> : null}
                    </RailSection>

                    {(p.eng1Info?.status || p.stcwBasicInfo?.status || p.stcwAdvancedInfo?.status || (p.qualifications && p.qualifications.length) || (p.otherQualifications && p.otherQualifications.length)) ? (
                        <RailSection accentColor={accentColor} title="Licenses">
                            {p.eng1Info?.status ? <RailBullet accentColor={accentColor} text="ENG 1" date={p.eng1Info.expiryDate} /> : null}
                            {p.stcwBasicInfo?.status ? <RailBullet accentColor={accentColor} text="STCW Basic" date={p.stcwBasicInfo.expiryDate} /> : null}
                            {p.stcwAdvancedInfo?.status ? <RailBullet accentColor={accentColor} text="STCW Advanced" date={p.stcwAdvancedInfo.expiryDate} /> : null}
                            {(p.qualifications || []).map((q, i) => <RailBullet key={`q-${i}`} accentColor={accentColor} text={q.name} />)}
                            {(p.otherQualifications || []).map((q, i) => <RailBullet key={`oq-${i}`} accentColor={accentColor} text={q} />)}
                        </RailSection>
                    ) : null}

                    {visas.length ? (
                        <RailSection accentColor={accentColor} title="Visas">
                            {visas.map((v, i) => <RailBullet key={i} accentColor={accentColor} text={v.label} date={v.date} />)}
                        </RailSection>
                    ) : null}

                    {p.skills?.length ? (
                        <RailSection accentColor={accentColor} title="Skills">
                            {p.skills.map((s, i) => <RailBullet key={i} accentColor={accentColor} text={s} />)}
                        </RailSection>
                    ) : null}

                    {showSection("equipment") && p.equipment?.length ? (
                        <RailSection accentColor={accentColor} title="Equipment">
                            {p.equipment.map((e, i) => <RailBullet key={i} accentColor={accentColor} text={e} />)}
                        </RailSection>
                    ) : null}
                </View>

                {/* ============ RIGHT BODY ============ */}
                <View style={styles.body}>
                    <Text style={styles.bodyName}>{p.firstName} {p.lastName}</Text>
                    {roleName ? <Text style={roleStyle}>{roleName.toUpperCase()}</Text> : null}

                    {p.aboutMe ? (
                        <View style={styles.bodySectionWrap}>
                            <SectionSeparator accentColor={accentColor} title="Profile" />
                            <Text style={base.paragraph}>{cleanProfileText(p.aboutMe)}</Text>
                        </View>
                    ) : null}

                    {showSection("objective") && p.lookingFor?.desiredRoles?.[0]?.position?.[0]?.name ? (
                        <View style={styles.bodySectionWrap}>
                            <SectionSeparator accentColor={accentColor} title="Objective" />
                            {p.lookingFor.desiredRoles.map((role, i) => (
                                <View key={i} style={{ marginBottom: i < p.lookingFor.desiredRoles.length - 1 ? 6 : 0 }}>
                                    <Text style={base.paragraph}>{constructObjective(role, i, p.lookingFor.desiredRoles)}</Text>
                                </View>
                            ))}
                        </View>
                    ) : null}

                    {p.pastRoles?.length ? (
                        <View style={styles.bodySectionWrap}>
                            <SectionSeparator accentColor={accentColor} title="Experience" />
                            {p.pastRoles.sort(latestFirst).map((role, i) => (
                                <View key={i} style={styles.bodyTimelineEntry} wrap={false}>
                                    <View style={styles.bodyTimelineHeader}>
                                        <View style={styles.bodyTimelineHeaderMain}>
                                            <Text style={styles.bodyTimelineRole}>{role.position}</Text>
                                            {role.companyName ? <Text style={styles.bodyTimelineCompany}>{role.companyName}</Text> : null}
                                        </View>
                                        <View style={styles.bodyTimelineDateBox}>
                                            <Text style={styles.bodyTimelineDate}>{handleTimePeriod(role.fromDate, role.toDate, role.present)}</Text>
                                        </View>
                                    </View>
                                    {role.description ? <Text style={styles.bodyTimelineDescription}>{cleanProfileText(role.description)}</Text> : null}
                                </View>
                            ))}
                        </View>
                    ) : null}

                    {p.education?.length ? (
                        <View style={styles.bodySectionWrap}>
                            <SectionSeparator accentColor={accentColor} title="Education" />
                            {p.education.sort(latestFirst).map((edu, i) => (
                                <View key={i} style={styles.bodyTimelineEntry} wrap={false}>
                                    <View style={styles.bodyTimelineHeader}>
                                        <View style={styles.bodyTimelineHeaderMain}>
                                            <Text style={styles.bodyTimelineRole}>{edu.name}</Text>
                                        </View>
                                        <View style={styles.bodyTimelineDateBox}>
                                            <Text style={styles.bodyTimelineDate}>{handleTimePeriod(edu.fromDate, edu.toDate, edu.present, true)}</Text>
                                        </View>
                                    </View>
                                    {edu.description ? <Text style={styles.bodyTimelineDescription}>{cleanProfileText(edu.description)}</Text> : null}
                                </View>
                            ))}
                        </View>
                    ) : null}

                    {showSection("hobbies") && p.hobbies ? (
                        <View style={styles.bodySectionWrap}>
                            <SectionSeparator accentColor={accentColor} title="Interests & Hobbies" />
                            <Text style={base.paragraph}>{cleanProfileText(p.hobbies)}</Text>
                        </View>
                    ) : null}

                    {showSection("references") && p.references?.length ? (
                        <View style={styles.bodySectionWrap}>
                            <SectionSeparator accentColor={accentColor} title="References" />
                            {p.references.map((reference, i) => {
                                const personParts = [reference.referenceName, reference.position].filter(Boolean);
                                return (
                                    <View key={i} style={styles.bodyReferenceItem} wrap={false}>
                                        {reference.companyName ? <Text style={base.referenceCompany}>{reference.companyName}</Text> : null}
                                        {personParts.length ? <Text style={base.referencePerson}>{personParts.join(" · ")}</Text> : null}
                                        {(reference.email || reference.contactNumber) ? (
                                            <View style={base.referenceContactRow}>
                                                {reference.email ? <Link style={base.referenceContactLink} href={`mailto:${reference.email}`}>{reference.email}</Link> : null}
                                                {reference.email && reference.contactNumber ? <Text style={base.referenceContactSep}>·</Text> : null}
                                                {reference.contactNumber ? <Text style={base.referenceContact}>{reference.contactNumber}</Text> : null}
                                            </View>
                                        ) : null}
                                    </View>
                                );
                            })}
                        </View>
                    ) : null}
                </View>
            </Page>
        </Document>
    );
};
